import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import styled, { css } from 'styled-components'
// components
import TreeComponent, { TreeWrapper } from 'components/Tree'
import PlanTableTree from 'components/PageComponents/Plans/Edit/components/ObjectivesList/components/PlanTableTree'
import ObjectivesTreeTheme from 'components/PageComponents/Plans/Edit/components/ObjectivesList/ObjectivesTreeTheme'
import { LoaderWithOverlay } from 'components/UI/Loader'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// constants
import { PLAN_SIDEBAR_INFO_WIDTH } from 'components/PageComponents/Plans/Edit/components/SidebarInfo/views'
import { Modals, LAYOUT_CONTAINER_WIDTH, NODE_CONTENT_RENDERER_SIZES, ENTITIES } from 'constants/index'
// context
import { LinkingModeFocuser } from 'containers/Plan/Edit/layers/API/Linking'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { CreateReviewBLContext } from 'containers/Review/CreateReview/layers/BL'
// hooks
import { useModalData, usePermissions } from 'hooks'
import { useCurrentUser } from 'hooks/useCurrentUser'
// themes
import { colors } from 'themes'
import { LayoutContainerInnerWrapper } from 'themes/views/layouts'
// lodash
import { concat, flow, get, isEmpty, isFunction, lowerCase, map } from 'lodash'
import { map as mapFp, orderBy, pipe } from 'lodash/fp'
// utils
import { transparentize } from 'polished'
import { mergePermissions } from 'utils/permissions'
import { applyTreeResourceTotals } from 'utils/objective'
import { getNodeKey, getKey, getParentKey } from 'utils/tree'
import { getFlatDataFromTree, getTreeFromFlatData } from 'react-sortable-tree'

// styled
const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${colors.white100};

  ${({ $overlap }) =>
    $overlap &&
    css`
      position: relative;
      z-index: 13001;
    `};

  ${TreeWrapper} {
    flex-grow: 1;
    padding-left: 12px;
  }

  .objectives-list-virtualized-class {
    > div {
      width: calc(100% - 10px) !important;
    }
  }
`

const Focuser = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1299;
  background: ${transparentize(0.5, colors.black)};
`

// constants
export const reactVirtualizedClassName = 'objectives-list-virtualized-class'

export const reactVirtualizedStyles = { outline: 'none', paddingBottom: '8px' }

export const Tree = TreeComponent

const ObjectivesList = ({
  entities,
  focuserState,
  generateCreateRootEntityNode,
  generateNewNode,
  getActiveCreateFormNode,
  getTableCreateFormNode,
  handleSetFocuserState,
  isDrawerExpanded,
  isLinkingMode,
  isLoading,
  plan,
  searchIndex,
  setDeepLevel,
  setSearchIndex,
  tableCreateForm,
  updatePlanTree
}) => {
  const {
    modalData: { node }
  } = useModalData({ modalType: Modals.CONFIRM_DELETE_PLAN_ITEM })

  const router = useRouter()

  const { isReview } = React.useContext(CreateReviewBLContext)
  const { entity: reviewEntity } = React.useContext(CreateReviewBLContext)
  const { showArchivedPlanItems, treeQueryLoading, isSidebarCollapsed } = React.useContext(PlanEditPageContext)

  const hasScrolledToReviewEntityRef = React.useRef(false)

  const { permissionsFromSecurityGroups } = useCurrentUser()

  const nodeEntityType = get(node, 'entityType')
  const canArchive = !showArchivedPlanItems && (
    (nodeEntityType === ENTITIES.PLAN_OBJECTIVE && permissionsFromSecurityGroups?.objectiveArchiveObjective) ||
    (nodeEntityType === ENTITIES.PLAN_ACTION && permissionsFromSecurityGroups?.actionArchiveAction) ||
    (nodeEntityType === ENTITIES.PLAN_KPI && permissionsFromSecurityGroups?.kpiArchiveKPI)
  )

  const { planAddAction, planAddKPI, planAddObjective, planModifyPlan } = get(plan, 'currentPermissions', {})

  const { setParentPermissions } = usePermissions({
    entity: plan,
    isPlan: true
  })

  const canAddPlanItems = planAddAction || planAddKPI || planAddObjective

  // methods
  const prepareSource = (source) => {
    const orderedFlatTree = orderBy('treePosition', 'asc')(source)

    let tree = getTreeFromFlatData({
      flatData: orderedFlatTree,
      getKey,
      getParentKey,
      rootKey: null
    })

    setDeepLevel(tree)

    if (isFunction(setParentPermissions)) {
      tree = setParentPermissions(tree)
    }

    const flatTreeWithLevels = pipe(
      (array) =>
        getFlatDataFromTree({
          treeData: array,
          getNodeKey,
          ignoreCollapsed: false
        }),
      mapFp('node')
    )(tree)

    const flatTree = flow(
      (array) => map(array, generateNewNode),
      (array) => {
        if (canAddPlanItems) {
          return concat(array, generateCreateRootEntityNode())
        }
        return array
      },
      // Re-inject the open nested create-form node so it survives a treeQueryRefetch rebuild.
      (array) => {
        const active = getActiveCreateFormNode?.()

        if (active?.node && active.parentId && array.some(({ id }) => id === active.parentId)) {
          return concat(array, active.node)
        }

        return array
      },
      // Inject the TableTree inline create-form node (nested under its parent) when one is open.
      // The parent is force-expanded, otherwise the form (and every item just created under it)
      // would stay hidden inside a collapsed branch.
      (array) => {
        const tableForm = getTableCreateFormNode?.()

        if (tableForm && tableForm.parentId && array.some(({ id }) => id === tableForm.parentId)) {
          const expandedArray = array.map((item) =>
            item.id === tableForm.parentId ? { ...item, expanded: true } : item
          )

          return concat(expandedArray, tableForm)
        }

        return array
      }
    )(flatTreeWithLevels)

    return applyTreeResourceTotals(
      getTreeFromFlatData({
        flatData: flatTree,
        getKey,
        getParentKey,
        rootKey: null
      })
    )
  }

  const canNodeHaveChildren = () => true

  const canDrop = ({ node, nextParent }) => !(node.referenced && nextParent)

  const canDrag = ({ node }) => {
    if (plan.locked || isReview) {
      return false
    }

    const permissions = [...get(node, 'entity.parentPermissions', []), ...get(node, 'entity.entityPermissions', [])]

    if (isEmpty(permissions)) {
      return !!planModifyPlan
    }

    const filteredPermissions = mergePermissions(permissions)

    const { actionModifyAction, kpiModifyKPI, objectiveModifyObjective } = filteredPermissions

    return planModifyPlan || actionModifyAction || kpiModifyKPI || objectiveModifyObjective
  }

  const synchronizeTree = ({ setTree, tree, formattedTree }) => {
    const planItems = formattedTree
      .map(({ id, position, expanded, parentId, referencedParentId }) => ({
        id,
        expanded,
        parentId: parentId || referencedParentId,
        treePosition: position
      }))
      .filter(({ id }) => typeof id === 'string')

    updatePlanTree(planItems)

    setDeepLevel(tree)
    setTree(tree)
  }

  const customSearchMethod = ({ searchQuery, node }) => searchQuery && node.treePosition === searchQuery

  // constants
  const sourceTree = React.useMemo(
    () => prepareSource(entities),
    [canAddPlanItems, entities, showArchivedPlanItems, tableCreateForm?.id]
  )

  const shouldShowFocuser =
    !isLoading && !treeQueryLoading && canAddPlanItems && sourceTree.length === 1

  const offset = isDrawerExpanded ? LAYOUT_CONTAINER_WIDTH.EXPANDED : LAYOUT_CONTAINER_WIDTH.COLLAPSED

  // Temporary flag to switch between the legacy react-sortable-tree and the new TableTree
  // for side-by-side comparison during migration. Enable with `?tableTree=true` in the URL.
  const useTableTree = router.query.tableTree === 'true'

  const onMoveNode = ({ treeData: tree, setTree }) => {
    let treeData = getTreeFromFlatData({
      flatData: tree,
      getKey,
      getParentKey,
      rootKey: null
    })

    const rootItem = treeData.find(({ isCreateRoot }) => isCreateRoot)

    treeData = setParentPermissions(treeData.filter(({ isCreateRoot }) => !isCreateRoot))
    setTree([...treeData, rootItem])
  }

  const searchFinishCallback = () => setSearchIndex(0)

  React.useEffect(() => {
    if (sourceTree.length === 1 && !router.query.planViewMode && canAddPlanItems) {
      handleSetFocuserState(true)
    } else {
      handleSetFocuserState(false)
    }
  }, [sourceTree, router])

  // In review-create mode (Plan tab of the review drawer) scroll the tree to the entity being reviewed.
  React.useEffect(() => {
    if (!isReview || isLoading || hasScrolledToReviewEntityRef.current) {
      return
    }

    const reviewEntityId = reviewEntity.id

    if (!reviewEntityId) {
      return
    }

    const targetNode = entities.find(({ entityId }) => entityId === reviewEntityId)

    if (targetNode && targetNode.treePosition) {
      setSearchIndex(targetNode.treePosition)
      hasScrolledToReviewEntityRef.current = true
    }
  }, [isReview, isLoading, entities, reviewEntity])

  return (
    <LayoutContainerInnerWrapper
      isPlan
      offset={isLinkingMode ? offset : isSidebarCollapsed ? 32 : PLAN_SIDEBAR_INFO_WIDTH}
      $isReview={isReview}>
      <LoaderWithOverlay isLoading={isLoading} />
      {!isLoading && (
        <>
          <Wrapper $overlap={shouldShowFocuser}>
            {useTableTree ? (
              <PlanTableTree
                treeData={sourceTree}
                isLoading={isLoading}
                canDrag={canDrag}
                canDrop={canDrop}
                updatePlanTree={updatePlanTree}
              />
            ) : (
              <Tree
                canDrag={canDrag}
                canDrop={canDrop}
                canNodeHaveChildren={canNodeHaveChildren}
                isLoading={isLoading}
                onMoveNode={onMoveNode}
                rowHeight={NODE_CONTENT_RENDERER_SIZES.ROW_HEIGHT}
                searchFinishCallback={searchFinishCallback}
                searchFocusOffset={0}
                searchMethod={customSearchMethod}
                searchQuery={searchIndex}
                sourceTree={sourceTree}
                synchronizeTreeFunc={synchronizeTree}
                theme={ObjectivesTreeTheme}
                reactVirtualizedListProps={{
                  className: reactVirtualizedClassName,
                  style: reactVirtualizedStyles
                }}
              />
            )}
          </Wrapper>

          {shouldShowFocuser && <Focuser />}
          {isLinkingMode && <LinkingModeFocuser />}
        </>
      )}

      <ConfirmDeleteModal
        header={`${canArchive ? 'Archive' : 'Delete'} ${get(node, 'title', '')}`}
        modalType={Modals.CONFIRM_DELETE_PLAN_ITEM}>
        {canArchive
          ? `Are you sure you want to archive this ${lowerCase(get(node, 'entityType', ''))}? This will also archive any sub-items.`
          : <span>Are you sure you want to delete this {lowerCase(get(node, 'entityType', ''))}? This will also <b>delete</b> any sub-items.</span>
        }
      </ConfirmDeleteModal>
    </LayoutContainerInnerWrapper>
  )
}

export default ObjectivesList
